// components/ui/ButtonIcon.tsx
import { FC } from "react";
import cx from "classnames";
import ArrowUpRight from "@/assets/icons/arrow-up-right";
import ShoppingBasket from "@/assets/icons/shopping-basket";
import Email from "@/assets/icons/email";
import AddReview from "@/assets/icons/add-review";
import Cart from "@/assets/icons/carts";
import { ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import giftIcon from "@/assets/icons/gift-white.svg";

type Icon =
  | "arrow"
  | "basket"
  | "email"
  | "rate"
  | "cart"
  | "arrow-right"
  | "none"
  | "gift";
type Theme = "dark" | "light";
export type { Icon, Theme };

interface ButtonIconProps {
  as?: React.ElementType;
  to?: string;
  targetId?: string;
  label: string;
  className?: string;
  buttonClassName?: string;
  theme: Theme;
  icon: Icon;
  iconWidth?: number;
  iconHeight?: number;
  submitType?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

const ButtonIcon: FC<ButtonIconProps> = ({
  as: Tag = "button",
  to,
  targetId,
  label,
  className,
  buttonClassName,
  theme,
  icon,
  iconWidth,
  iconHeight,
  submitType,
  disabled,
  onClick,
}) => {
  const location = useLocation();

  const handleClick = () => {
    // If onClick is provided, call it
    if (onClick) {
      onClick();
    }
    // If targetId is provided, scroll to it (existing functionality)
    if (targetId) {
      const section = document.getElementById(targetId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    } else if (location.pathname === to) {
      window.scrollTo({ behavior: "smooth", top: 0 });
    }
  };

  return (
    <Tag
      {...(to && { to })}
      {...(submitType && { type: "submit" })}
      {...(disabled && { disabled })}
      onClick={handleClick}
      style={{ position: "relative", paddingRight: "48px" }}
      className={cx(
        className,
        buttonClassName,
        "group flex cursor-pointer items-center gap-[10px] rounded-[40px] p-2 pr-[40px] disabled:cursor-auto disabled:opacity-70 md:gap-[14px]",
        theme === "dark" && !buttonClassName
          ? "bg-primary hover:enabled:bg-primary/90"
          : !buttonClassName
            ? "bg-sand hover:enabled:brightness-95"
            : "",
      )}
    >
      <span
        style={{ lineHeight: "40px" }}
        className={cx(
          "flex-nowrap px-4 font-medium whitespace-nowrap md:text-lg",
          theme === "dark" ? "text-primary" : "text-dark",
        )}
      >
        {label}
      </span>
      <div
        style={{ position: "absolute", right: "8px", transition: "all 0.3s" }}
        className={cx(
          "flex h-[40px] w-[40px] items-center justify-center rounded-full",
          theme === "dark" ? "bg-primary" : "bg-dark",
          "group-hover:w-[calc(100%-16px)]",
        )}
      >
        {icon === "arrow" && (
          <ArrowUpRight
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "arrow-right" && (
          <ArrowRight
            className="w-[19px] md:w-[24px]"
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "basket" && (
          <ShoppingBasket
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "email" && (
          <Email
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "rate" && (
          <AddReview
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "cart" && (
          <Cart
            width={iconWidth}
            height={iconHeight}
            color={
              theme === "dark" ? "var(--color-primary)" : "var(--color-sand)"
            }
          />
        )}
        {icon === "gift" && (
          <img
            src={giftIcon}
            width={iconWidth}
            height={iconHeight}
            alt="gift icon"
          />
        )}
      </div>
    </Tag>
  );
};

export default ButtonIcon;
